import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentCompetencyProficiency } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { fetchContentNuggetsByCompetencyId } from '@backrs_ui/grow-ui/src/redux/contentNugget/thunks/nuggetsByCompetency';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';
import { postProficiencyForCompetency } from '@backrs_ui/grow-ui/src/redux/user/thunks/competencyProficiency';

import { Button, Box } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { TailSpin } from 'react-loader-spinner';

import { CompetencyPage } from './CompetencyPage';

const COMPETENCY_STRINGS = appStrings.modules.competency;

const fetchNuggetsForThisCompetency = async (competency_id: number, dispatch: AppDispatch) => {
  try {
    await dispatch(fetchContentNuggetsByCompetencyId({ competency_id }));
  } catch (error) {
    console.error('Failed to fetch content competencies:', error);
  }
};

export const CompetencyPageContainer = () => {
  const { slug } = useParams<{ slug: string }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const contentCompetencyNuggets = useAppSelector((state) => state.contentNugget.currentNuggetList);

  const contentCompetencies = useAppSelector(
    (state) => state.contentCompetency.contentCompetencies,
  );
  const user = useAppSelector((state) => state.user.user);

  const { loading: nuggetsLoading } = useAppSelector((state) => state.contentNugget);

  const contentCompetency = contentCompetencies.find((competency) => competency.slug === slug);

  const competencyCategoryName = useAppSelector(
    (state) =>
      state.contentCategory?.contentCategories?.find(
        (category) => category.id === contentCompetency?.content_category_id,
      )?.title,
  );

  const currentProficiencyLevel = user?.competency_proficiencies?.find(
    (proficiency) => proficiency.content_competency_id === contentCompetency?.id,
  )?.proficiency;

  useEffect(() => {
    (async () => {
      if (contentCompetency) {
        await fetchNuggetsForThisCompetency(contentCompetency.id, dispatch);
      }
    })();
  }, [contentCompetency, dispatch]);

  const isLoading = !contentCompetencies || !user || nuggetsLoading;

  if (isLoading) {
    return (
      <Box sx={loadingSpinnerStyle}>
        <TailSpin color="#000" height={50} width={50} />
      </Box>
    );
  }

  if (slug === undefined) {
    return (
      <div>
        {COMPETENCY_STRINGS.pageErrors.invalidId}
        <Button onClick={() => navigate(APP_ROUTE.PROSPER)} variant="outlined">
          {COMPETENCY_STRINGS.pageErrors.returnHome}
        </Button>
      </div>
    );
  }

  if (!contentCompetency || !competencyCategoryName) {
    return (
      <div>
        {COMPETENCY_STRINGS.pageErrors.competencyNotFound}
        <Button onClick={() => navigate(APP_ROUTE.PROSPER)} variant="outlined">
          {COMPETENCY_STRINGS.pageErrors.returnHome}
        </Button>
      </div>
    );
  }

  const handleUpdatingCompetencyProficiency = ({
    content_competency_id,
    proficiency,
  }: ContentCompetencyProficiency) => {
    dispatch(postProficiencyForCompetency({ content_competency_id, proficiency }));
  };

  return (
    <CompetencyPage
      categoryName={competencyCategoryName}
      contentCompetency={contentCompetency}
      nuggets={contentCompetencyNuggets}
      completedNuggetIds={user.completed_nugget_ids}
      setProficiencyLevel={handleUpdatingCompetencyProficiency}
      currentProficiencyLevel={currentProficiencyLevel || 0}
    />
  );
};

const loadingSpinnerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
};
